import React from 'react'
import {
  Box,
  Flex,
  HStack,
  Image as ChakraImage,
  Icon,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spacer,
  Text as ChakraText,
  VStack,
} from '@chakra-ui/react'
import numeral from 'numeral'
import { IoEllipsisVerticalSharp } from 'react-icons/io5'
import { FiExternalLink } from 'react-icons/fi'

import { useTranslation } from 'react-i18next'
import { colors } from '../../../ui-kit'
import { getOriginIconUrl, sourceTypeMapping } from '../../../utils/source'
import type { SourcesResponse } from '../../interfaces'
import PermissionGuard from '../../components/PermissionGuard'
import { PermissionsEnum, encodeHash } from '../../../utils'
import { sourceTypeEnum } from '../../components/ExternalStorage/utils'

interface Props {
  source: SourcesResponse
  rank: number
  handleRemoveSource?(): void
}

interface SourceInfoProps {
  source: SourcesResponse
}

const SourceInfo = ({ source }: SourceInfoProps): React.ReactElement => {
  if (source.isDefault) {
    return <></>
  }

  if (source.typeId === 2) {
    return <>{source.baseUrl ? source.baseUrl : 'Absolute URL fetch'}</>
  }
  if ([1, 3, 7].includes(Number(source.typeId)) && source.s3Bucket) {
    return <>{source.s3Bucket}</>
  }
  if (source.typeId === 5 && source.bucketName) {
    return <>{source.bucketName}</>
  }
  if (source.typeId === 6 && source.containerName) {
    return <>{source.containerName}</>
  }

  return <></>
}

const SourceName = ({ source }: SourceInfoProps): React.ReactElement => {
  if (source.isDefault) {
    return <>{source.name}</>
  }

  return (
    <Link
      href={`/dashboard/external-storage/${encodeHash(source.id)}`}
      target="_blank"
      rel="noopener noreferrer"
      color={colors.appBlue}
      fontWeight="500"
      textDecoration="none"
      display="inline-flex"
      alignItems="center"
      gap="4px"
      _hover={{ 
        textDecoration: 'underline',
        '& .external-icon': {
          opacity: 1
        }
      }}
    >
      {source.name}
      <Icon 
        as={FiExternalLink} 
        boxSize="14px" 
        className="external-icon"
        opacity={0}
        transition="opacity 0.2s"
        color={colors.appBlue}
      />
    </Link>
  )
}

const SourceTypeText = ({ source }: SourceInfoProps): React.ReactElement => {
  const { t } = useTranslation()

  if (source.typeId === 2 && !source.baseUrl) {
    return <>{t(sourceTypeMapping[8].name)}</>
  } else if (source.typeId === Number(sourceTypeEnum.AMAZON_S3) && source.endpoint) {
    return <>{t(sourceTypeMapping[sourceTypeEnum.S3_COMPATIBLE_STORAGE].name)}</>
  }

  return <>{t(sourceTypeMapping[source.typeId].name)}</>
}

const PreferenceListItem = ({
  source,
  rank,
  handleRemoveSource,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <HStack spacing={4} alignItems="center" width="full">
      <ChakraText
        minWidth="6%"
        maxWidth="6%"
        fontWeight="medium"
        color={colors.appBlueyGrey}
        borderTop="1px"
        borderBottom="1px"
        borderColor="#e1e4eb"
        padding="2px 4px"
        align="center"
      >
        {numeral(rank).format('0o')}
      </ChakraText>

      <Box
        flex={1}
        minWidth={0} // prevents from overflowing parent container
        borderWidth="1px"
        borderRadius="3px"
        borderColor={colors.appVeryLightBlue}
        bgColor={colors.appPaleGrey}
        padding={4}
      >
        <Flex width="full" flexDirection="row" alignItems="center">
          <HStack width="90%" spacing="0.5em">
            {source.isDefault ? (
              <Box minW="26px" />
            ) : (
              <Box>
                <Icon
                  marginRight="-0.75em"
                  boxSize="20px"
                  color={colors.appBlueyGrey}
                  as={IoEllipsisVerticalSharp}
                />
                <Icon
                  boxSize="20px"
                  color={colors.appBlueyGrey}
                  as={IoEllipsisVerticalSharp}
                />
              </Box>
            )}

            <ChakraImage
              alt="origin icon"
              boxSize="56px"
              src={getOriginIconUrl(source)}
              margin="1em 0.5em"
              padding="0.25em"
              fallbackSrc="https://ik.imagekit.io/demo/img/icons8-server-64_sibZOIGTb.png"
            />

            <VStack width="60%" spacing="0.1em" alignItems="start">
              <ChakraText
                isTruncated
                width="full"
                fontWeight="500"
                color={colors.appSlate}
              >
                <SourceName source={source} />
              </ChakraText>
              {!source.isDefault && (
                <>
                  <ChakraText isTruncated width="full" color={colors.appSlate} fontWeight="400">
                    <SourceTypeText source={source} />
                  </ChakraText>
                  <ChakraText
                    isTruncated
                    width="full"
                    color={colors.appBlueyGrey}
                    fontWeight="400"
                  >
                    <SourceInfo source={source}></SourceInfo>
                  </ChakraText>
                </>
              )}
            </VStack>
          </HStack>

          <Spacer />

          <PermissionGuard
            permission={PermissionsEnum.SELF_INTEGRATION_MANAGE}
          >
            {
              (hasPermission: boolean) => (
                (hasPermission && !source.isDefault) ? (
                  <Menu>
                    <MenuButton as={Box}>
                      <Icon
                        boxSize="20px"
                        color={colors.appBattleshipGrey}
                        as={IoEllipsisVerticalSharp}
                      />
                    </MenuButton>
                    <MenuList>
                      <MenuItem onClick={handleRemoveSource}>
                        {t('remove_from_list', 'Remove From List')}
                      </MenuItem>
                    </MenuList>
                  </Menu>
                ) : <></>
              )
            }
          </PermissionGuard>
        </Flex>
      </Box>
    </HStack>
  )
}

export default PreferenceListItem
